import React, { useState } from 'react';
import { Sidebar } from '../../components/Sidebar';
import { Header } from '../../components/Header';
import { StepIndicator } from '../../components/StepIndicator';
import { DownloadIcon, ImageIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Select Product', 'Background', 'Greetings', 'Store Info', 'Generate', 'Output Format'];
export function ChooseOutputFormat() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<'image' | 'video' | 'both' | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['1:1']);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const toggleFormat = (format: string) => {
    setSelectedFormats(prev => prev.includes(format) ? prev.filter(f => f !== format) : [...prev, format]);
  };
  const handleGenerate = () => {
    if (selectedOption === 'video' || selectedOption === 'both') {
      setIsGeneratingVideo(true);
      const interval = setInterval(() => {
        setVideoProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsGeneratingVideo(false);
            setIsComplete(true);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    } else {
      setIsComplete(true);
    }
  };
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-1477">
      <Sidebar data-id="element-1478" />
      <main className="flex-1" data-id="element-1479">
        <Header data-id="element-1480" />
        <StepIndicator currentStep={6} steps={STEPS} data-id="element-1481" />
        <div className="px-6 py-8" data-id="element-1482">
          <div className="max-w-5xl mx-auto" data-id="element-1483">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-1484">
              Step 6: Choose Output Format
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-1485">
              Download as image, generate AI video, or get both
            </p>

            {!isComplete ? <div className="space-y-6" data-id="element-1486">
                <div className="grid grid-cols-3 gap-6" data-id="element-1487">
                  {/* Image Option */}
                  <button onClick={() => setSelectedOption('image')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'image' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1488">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-1489">
                      <ImageIcon className="text-blue-500" size={24} data-id="element-1490" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1491">
                      Download Image
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1492">
                      Get static creative in multiple formats
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1493">
                      <span className="text-xs text-gray-500" data-id="element-1494">⏱️ Instant</span>
                      {selectedOption === 'image' && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-1495" />}
                    </div>
                  </button>

                  {/* Video Option */}
                  <button onClick={() => setSelectedOption('video')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'video' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1496">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4" data-id="element-1497">
                      <VideoIcon className="text-purple-500" size={24} data-id="element-1498" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1499">
                      Generate AI Video
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1500">
                      8-second video with animations
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1501">
                      <span className="text-xs text-gray-500" data-id="element-1502">⏱️ 2 mins</span>
                      {selectedOption === 'video' && <CheckCircleIcon className="text-purple-500" size={20} data-id="element-1503" />}
                    </div>
                  </button>

                  {/* Both Option */}
                  <button onClick={() => setSelectedOption('both')} className={`bg-white rounded-lg border-2 p-6 text-left transition-all ${selectedOption === 'both' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1504">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-1505">
                      <DownloadIcon className="text-green-500" size={24} data-id="element-1506" />
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2" data-id="element-1507">
                      Download Both
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1508">
                      Get image + video versions
                    </p>
                    <div className="flex items-center justify-between" data-id="element-1509">
                      <span className="text-xs text-gray-500" data-id="element-1510">⏱️ 2 mins</span>
                      {selectedOption === 'both' && <CheckCircleIcon className="text-green-500" size={20} data-id="element-1511" />}
                    </div>
                  </button>
                </div>

                {selectedOption && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1512">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1513">
                      Select Output Formats
                    </h3>
                    <div className="grid grid-cols-3 gap-4" data-id="element-1514">
                      {[{
                  id: '1:1',
                  name: 'Square (1:1)',
                  desc: 'Facebook Feed, Instagram Post'
                }, {
                  id: '9:16',
                  name: 'Portrait (9:16)',
                  desc: 'Instagram Story, YouTube Shorts'
                }, {
                  id: '16:9',
                  name: 'Landscape (16:9)',
                  desc: 'Google Display, YouTube'
                }].map(format => <button key={format.id} onClick={() => toggleFormat(format.id)} className={`p-4 rounded-lg border-2 transition-all text-left ${selectedFormats.includes(format.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1515">
                          <div className="flex items-center justify-between mb-2" data-id="element-1516">
                            <span className="font-medium text-gray-800 text-sm" data-id="element-1517">
                              {format.name}
                            </span>
                            {selectedFormats.includes(format.id) && <CheckCircleIcon className="text-blue-500 flex-shrink-0" size={18} data-id="element-1518" />}
                          </div>
                          <p className="text-xs text-gray-500" data-id="element-1519">{format.desc}</p>
                        </button>)}
                    </div>
                  </div>}

                {(selectedOption === 'video' || selectedOption === 'both') && <div className="bg-purple-50 border border-purple-200 rounded-lg p-6" data-id="element-1520">
                    <h4 className="font-medium text-purple-900 mb-3" data-id="element-1521">
                      AI Video Features
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm text-purple-800" data-id="element-1522">
                      <div data-id="element-1523">
                        <p className="font-medium mb-1" data-id="element-1524">Product Animation</p>
                        <p className="text-xs" data-id="element-1525">
                          3D rotation, movement, zoom effects
                        </p>
                      </div>
                      <div data-id="element-1526">
                        <p className="font-medium mb-1" data-id="element-1527">Background Motion</p>
                        <p className="text-xs" data-id="element-1528">
                          Parallax scrolling, cinematic panning
                        </p>
                      </div>
                      <div data-id="element-1529">
                        <p className="font-medium mb-1" data-id="element-1530">Text Animations</p>
                        <p className="text-xs" data-id="element-1531">Fade in/out at 2-3s and 6-7s</p>
                      </div>
                      <div data-id="element-1532">
                        <p className="font-medium mb-1" data-id="element-1533">Duration</p>
                        <p className="text-xs" data-id="element-1534">8 seconds (social optimized)</p>
                      </div>
                    </div>
                  </div>}

                {isGeneratingVideo && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1535">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1536">
                      Generating AI Video...
                    </h3>
                    <div className="mb-4" data-id="element-1537">
                      <div className="flex justify-between text-sm text-gray-600 mb-2" data-id="element-1538">
                        <span data-id="element-1539">Progress</span>
                        <span data-id="element-1540">{videoProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-1541">
                        <div className="bg-purple-500 h-3 rounded-full transition-all duration-300" style={{
                    width: `${videoProgress}%`
                  }} data-id="element-1542"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1543">
                      Creating professional video with animations...
                    </p>
                  </div>}

                <div className="flex justify-between" data-id="element-1544">
                  <button onClick={() => navigate('/dealer/ai-generator/step-5')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1545">
                    Back
                  </button>
                  <button onClick={handleGenerate} disabled={!selectedOption || selectedFormats.length === 0 || isGeneratingVideo} className="px-8 py-3 bg-green-500 text-white rounded-md font-medium hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-1546">
                    {isGeneratingVideo ? 'Generating...' : 'Generate & Download'}
                  </button>
                </div>
              </div> : <div className="space-y-6" data-id="element-1547">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-1548">
                  <div className="flex items-center" data-id="element-1549">
                    <CheckCircleIcon className="text-green-500 mr-3 flex-shrink-0" size={32} data-id="element-1550" />
                    <div data-id="element-1551">
                      <h2 className="text-xl font-semibold text-green-900" data-id="element-1552">
                        Creatives Ready!
                      </h2>
                      <p className="text-sm text-green-700" data-id="element-1553">
                        Your{' '}
                        {selectedOption === 'both' ? 'image and video' : selectedOption}{' '}
                        creatives are ready to download
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1554">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1555">
                    Download Files
                  </h3>
                  <div className="grid grid-cols-3 gap-4" data-id="element-1556">
                    {selectedFormats.map(format => <div key={format} className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center" data-id="element-1557">
                        <DownloadIcon className="mx-auto text-gray-600 mb-2" size={24} data-id="element-1558" />
                        <p className="font-semibold text-gray-800" data-id="element-1559">{format}</p>
                        <p className="text-xs text-gray-600 mt-1" data-id="element-1560">
                          {selectedOption === 'both' ? 'Image + Video' : selectedOption}
                        </p>
                      </div>)}
                  </div>
                </div>

                <div className="flex justify-center space-x-4" data-id="element-1561">
                  <button className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium transition-colors" data-id="element-1562">
                    Download All Files
                  </button>
                  <button onClick={() => navigate('/creative-customizer')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1563">
                    Create Another
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}